import styled, { css, keyframes } from 'styled-components';

import { AFFILIATE_FORM, ANIM, CASINO_SETTINGS, COLORS, MOBILE, REDUCED_MOTION, SPACE } from '../../../../theme/tokens';
import { Text } from '../../../../components';

// Section header (mirrors the shared SectionTitle) — but with the brand logo mark
// in place of an Icon-set glyph, which SectionTitle can't take.
export const LogosHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
`;

export const LogosTitle = styled(Text)`
  font-size: 20px;
  font-weight: 500;
  color: ${COLORS.heading};
`;

// Logo-window status banner — green while the logos can still be replaced, gold once
// the registered-player cap has locked them.
export const Banner = styled.div<{ $tone: 'ok' | 'lock' }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  margin-bottom: 18px;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$tone === 'ok' ? CASINO_SETTINGS.okBorder : CASINO_SETTINGS.lockBorder)};
  background: ${(p) => (p.$tone === 'ok' ? CASINO_SETTINGS.okBg : CASINO_SETTINGS.lockBg)};
`;

export const BannerIcon = styled.span`
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  margin-top: 1px;
`;

export const BannerText = styled(Text)`
  font-size: 13px;
  font-weight: 400;
  line-height: 1.45;
  color: ${COLORS.text};
`;

// --- Primary logo showcase ---------------------------------------------------
export const PrimaryRow = styled.div`
  padding: 4px 0 20px;
  border-bottom: 1px solid ${COLORS.hairline};
`;

/* The primary logo and its controls, side by side: the FTUX's light hugs this, never the divider below. */
export const PrimaryBody = styled.div`
  display: flex;
  align-items: center;
  gap: 22px;

  ${MOBILE} {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

export const PrimaryFrameWrap = styled.div`
  flex: 0 0 auto;
  width: 320px;

  ${MOBILE} {
    width: 100%;
  }
`;

// A logo preview frame — FIXED height so the logo is contained (its own size never
// dictates the frame), giving a coherent, even grid. Light artwork sits on a dark
// wash and vice-versa. Flex-centred so the (max-bounded) image letterboxes within the
// padding and never reaches the rounded corners.
// The plate is animated, not swapped: a replacement can flip which backing gives the new
// artwork contrast (a white wordmark arriving where a black one was), and that flip lands
// in the same beat as the artwork cross-fade above it. `useAutoFrame` holds the plate in
// use until the replacement has been measured, so this only ever runs once per swap.
export const Frame = styled.div<{ $frame: 'light' | 'dark'; $tall?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: ${(p) => (p.$tall ? 150 : 116)}px;
  padding: ${(p) => (p.$tall ? 20 : 16)}px;
  border-radius: 12px;
  background: ${(p) => (p.$frame === 'dark' ? CASINO_SETTINGS.previewDark : CASINO_SETTINGS.previewLight)};
  overflow: hidden;
  transition: background-color ${ANIM.LOGO_SWAP_MS}ms ease;

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

// Fills the frame's content box (the flex child), so the artwork layers inside it can
// stack and overlap for the cross-fade rather than sitting side by side.
export const ArtStack = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

// Contain the logo no matter its aspect: cap both axes to the (definite) stack and leave
// width/height auto so a small logo renders at its own size instead of being blown up to
// fill the tile. `inset: 0` + `margin: auto` is what centres a replaced element that is
// sized by its own intrinsic box, which is what lets both layers occupy the same place.
//
// $dim = this copy is on its way out: either a replacement run is rewriting the logo, or
// its successor has loaded and is fading in over it. Either way the artwork on screen is
// still the OLD one (the site is still serving it), so it is softened rather than removed
// — blurring back is what makes the tile read as "this is being redrawn" instead of "this
// logo is broken". Mirrors the deploy step, where the site preview blurs under the same
// grid. The frame's own overflow clip keeps the blur's feathered edge inside the corners.
//
// $enter = the incoming copy, painted over the outgoing one and lit once it has decoded.
//
// $fill = artwork that is tiny by design (the 64 px favicon): scaled up to the frame's full
// height, the height its neighbours are capped at, rather than kept at its own size and
// reading as a smaller logo. `contain` keeps a non-square file undistorted.
export const PreviewImg = styled.img<{ $dim?: boolean; $enter?: boolean; $lit?: boolean; $fill?: boolean }>`
  position: absolute;
  inset: 0;
  margin: auto;
  display: block;
  width: auto;
  height: ${(p) => (p.$fill ? '100%' : 'auto')};
  max-width: 100%;
  max-height: 100%;
  min-width: 0;
  min-height: 0;
  object-fit: contain;
  filter: ${(p) => (p.$dim ? 'blur(6px)' : 'none')};
  opacity: ${(p) => (p.$enter ? (p.$lit ? 1 : 0) : p.$dim ? 0.7 : 1)};
  transition:
    filter ${ANIM.LOGO_FIELD_FADE_MS}ms ease,
    opacity ${(p) => (p.$enter ? ANIM.LOGO_SWAP_MS : ANIM.LOGO_FIELD_FADE_MS)}ms ease;

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

// The drifting dot field over a logo that is currently being rewritten — the same
// animated lattice the onboarding deploy step runs over the website previews, reused
// here at tile scale (see the shared DotField canvas). Transparent: the frame's plate
// is its backdrop and the softened artwork shows through beneath the dots, so the tile
// stays recognisably ITS logo while the run is in flight. Cross-fades in and out, then
// unmounts, which is what stops the canvas animating.
export const FieldOverlay = styled.div<{ $shown: boolean }>`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: ${(p) => (p.$shown ? 1 : 0)};
  transition: opacity ${ANIM.LOGO_FIELD_FADE_MS}ms ease-in-out;

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

export const PrimaryMeta = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

// The primary logo's name and its origin label, on one line.
export const PrimaryHead = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

export const PrimaryLabel = styled(Text)`
  font-size: 16px;
  font-weight: 700;
  color: ${COLORS.textStrong};
`;

export const PrimaryNote = styled(Text)`
  font-size: 13px;
  font-weight: 300;
  line-height: 1.45;
  color: ${COLORS.textMuted};
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACE[12]};
  margin-top: ${SPACE[8]};

  /* Phones: Replace logo comes first, at its own width and on one line; Download takes the rest of the row (the user,
     2026-10-20), or a row of its own on a screen too narrow for both. Replace logo sits in the FTUX's target span. */
  ${MOBILE} {
    & > span {
      flex: none;
    }

    & > span > button {
      white-space: nowrap;
    }

    & > button {
      flex: 1 1 auto;
    }
  }
`;

export const LockNote = styled(Text)`
  margin-top: 6px;
  font-size: 12px;
  font-weight: 300;
  color: ${CASINO_SETTINGS.lockFg};
`;

// --- Origin label ------------------------------------------------------------
// Every logo says where it came from: the branding pipeline ("Lumio generated", neutral)
// or the owner's own upload ("Custom", accent-tinted so a replaced logo stands out in a
// grid of generated ones). A quiet caption-sized pill — it annotates the logo, it isn't
// a control, so it never competes with the tile's Replace / Download buttons.
export const OriginPill = styled.span<{ $origin: 'generated' | 'custom' }>`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 7px 2px 5px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$origin === 'custom' ? CASINO_SETTINGS.cardSelectedBorder : COLORS.hairline)};
  background: ${COLORS.surfaceFaint};
  color: ${(p) => (p.$origin === 'custom' ? COLORS.accent : COLORS.textFaint)};
`;

// The Lumio mark on the "Lumio generated" pill — the same multicolour asset the Lumio
// panel header and the replace overlay's switches use. A gradient asset, so it is a plain
// <img>, not an Icon glyph (a flat grey star read as decoration, not as the assistant).
export const OriginMark = styled.img`
  flex: 0 0 auto;
  display: block;
  width: 12px;
  height: 12px;
`;

export const OriginText = styled(Text)`
  font-size: 10px;
  font-weight: 600;
  line-height: 14px;
  letter-spacing: 0.02em;
  white-space: nowrap;
  color: inherit;
`;

// --- Variants grid -----------------------------------------------------------
// The grid's own header. Its bottom margin clears the first row's TITLE, which sits right
// under it, so the "Download all" button and that title never read as one group.
export const VariantsHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 22px 0 20px;
`;

export const VariantsLabel = styled(Text)`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${COLORS.textFaint};
`;

// Shared variant-grid template — reused by the real grid and its skeleton so the
// two can't drift apart (and the layout doesn't jump on reveal).
// The ROW gap is much larger than the column gap on purpose: each tile now starts with
// its name, so a tight row gap puts the next tile's title straight under the previous
// row's buttons and the two rows read as one block.
const variantGrid = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 28px 14px;

  ${MOBILE} {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 24px 12px;
  }
`;

export const Grid = styled.div`
  ${variantGrid}
`;

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

// The controls line under the preview: the compact Replace + Download icon buttons
// (mirrors the DNS field row's value + copy button — far lighter than full buttons per
// tile, which would double every card's height), then the origin label inline beside
// them, centred on the same line. It wraps rather than overflowing on the narrowest tile.
export const CardMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

export const CardActions = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 4px;
`;

// 30px, not the usual 32: the buttons share their line with the origin label, and on the
// narrowest tile in the grid those two only fit together with a few pixels to spare.
export const TileBtn = styled.button`
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 8px;
  background: ${COLORS.surfaceFaint};
  color: ${COLORS.textMuted};
  cursor: pointer;
  transition:
    background ${ANIM.INTERACT_MS}ms,
    color ${ANIM.INTERACT_MS}ms;

  &:hover:not(:disabled) {
    background: ${COLORS.surfaceMuted};
    color: ${COLORS.text};
  }

  &:disabled {
    color: ${COLORS.textFaint};
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
  }
`;

// The tile's name plus its "what is this for" button. Anchors the popover, which spans
// the tile's full width so it can never be clipped by the grid column or the panel edge.
export const CardHead = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
`;

export const InfoBtn = styled.button`
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: none;
  color: ${COLORS.textFaint};
  cursor: pointer;
  transition: color ${ANIM.INTERACT_MS}ms;

  &:hover,
  &[aria-expanded='true'] {
    color: ${COLORS.text};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 1px;
  }
`;

// Click-opened, so it stays put until dismissed rather than vanishing on pointer-out.
export const InfoPop = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 2;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceCard};
  box-shadow: ${AFFILIATE_FORM.modalShadow};
`;

export const InfoPopText = styled(Text)`
  font-size: 12px;
  font-weight: 300;
  line-height: 1.45;
  color: ${COLORS.textMuted};
`;

export const CardLabel = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// --- Skeletons ---------------------------------------------------------------
export const SkelStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

export const SkelGrid = styled.div`
  ${variantGrid}
`;

// --- Placeholder (real logos not generated yet) ------------------------------
// Shown instead of the schematic fallback while the casino's logos are still being
// produced (or aren't available yet). An honest "your logos will appear here" state:
// a dashed neutral mark + copy, then ghost variant tiles that mirror the real grid
// so nothing shifts on reveal. Deliberately distinct from the (solid, shimmering)
// loading skeleton — dashed outlines read as "to come", not "loading".
const placeholderPulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
`;

export const PlaceholderWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 8px 0 4px;
`;

export const PlaceholderBadge = styled.div<{ $pulse?: boolean }>`
  display: grid;
  place-items: center;
  width: 64px;
  height: 64px;
  margin-bottom: 4px;
  border-radius: 16px;
  border: 1px dashed ${CASINO_SETTINGS.previewBorder};
  background: ${COLORS.surfaceFaint};

  ${(p) =>
    p.$pulse
      ? css`
          animation: ${placeholderPulse} ${ANIM.LOGO_PLACEHOLDER_PULSE_MS}ms ${ANIM.EASE} infinite;
        `
      : ''}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const PlaceholderTitle = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  color: ${COLORS.textStrong};
`;

export const PlaceholderText = styled(Text)`
  max-width: 460px;
  font-size: 13px;
  font-weight: 300;
  line-height: 1.5;
  color: ${COLORS.textMuted};
`;

export const PlaceholderGrid = styled.div`
  ${variantGrid}
  width: 100%;
  margin-top: 18px;
`;

// A ghost variant tile — same footprint as a real Card's Frame, but a dashed
// neutral well holding the image-placeholder glyph (never artwork). Positioned + clipped so
// the regeneration field can run inside it while the first set is being drawn.
export const GhostFrame = styled.div`
  position: relative;
  display: grid;
  place-items: center;
  width: 100%;
  height: 116px;
  border-radius: 12px;
  border: 1px dashed ${CASINO_SETTINGS.previewBorder};
  background: ${COLORS.surfaceFaint};
  color: ${COLORS.textFaint};
  overflow: hidden;
`;

// The placeholder glyph, lifted above the field running behind it so the well still reads as
// "artwork goes here" rather than dissolving into the lattice.
export const GhostGlyph = styled.span`
  position: relative;
  z-index: 1;
  display: grid;
  place-items: center;
`;

export const GhostLabel = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.textMuted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
